'use client';

import { useEffect, useState } from 'react';
import { paths } from 'src/routes/paths';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, Container, Typography } from '@mui/material';
import { useRouter } from 'next/navigation';

import { SimpleLayout } from 'src/layouts/simple';
import { Form, RHFTextField, RHFUpload } from 'src/components/hook-form';
import axiosInstance, { endpoints } from 'src/lib/axios';
import { hämtaSenasteFel, type Felpost } from 'src/lib/fel/felbutik';

/**
 * Fristående rapportsida, nåbar från /fel och från menyn.
 *
 * Sidan läste tidigare `sessionStorage['last_server_error']` — en nyckel som ingenting i
 * kodbasen någonsin skrev, vilket gjorde att traceId alltid blev 'unknown' och felmeddelandet
 * alltid null. Den hämtar nu felet ur felbutiken, som axios-interceptorn och kraschgränserna
 * faktiskt fyller.
 *
 * De flesta användare möter i stället FelrapportDialog, som öppnas direkt där felet inträffade.
 */
export default function RapporteraProblemPage() {
    const router = useRouter();
    const [fel, setFel] = useState<Felpost | null>(null);
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState(false);

    useEffect(() => {
        setFel(hämtaSenasteFel());
    }, []);

    const Schema = z.object({
        vadJagGjorde: z.string().optional(),
        vadHände: z.string().min(1, 'Beskriv gärna vad som hände'),
        filer: z.custom<File[]>().optional(),
    });

    const methods = useForm({
        resolver: zodResolver(Schema),
        defaultValues: { vadJagGjorde: '', vadHände: '', filer: [] },
    });

    const { handleSubmit, formState: { isSubmitting } } = methods;

    const onSubmit = async (data: any) => {
        setSubmitError('');

        if (!fel?.referens) {
            setSubmitError(
                'Det finns inget nyligen inträffat fel att rapportera. Öppna den här sidan från felmeddelandet du fick, så följer referensen med.'
            );
            return;
        }

        try {
            const formData = new FormData();
            formData.append(
                'rapport',
                new Blob(
                    [JSON.stringify({ vadJagGjorde: data.vadJagGjorde, vadHände: data.vadHände })],
                    { type: 'application/json' }
                )
            );

            if (data.filer?.length) {
                Array.from(data.filer as File[]).forEach((file) => formData.append('filer', file));
            }

            await axiosInstance.post(endpoints.fel.rapport(fel.referens), formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            setSubmitSuccess(true);
        } catch {
            setSubmitError('Kunde inte skicka rapporten. Försök igen.');
        }
    };

    if (submitSuccess) {
        return (
            <SimpleLayout>
                <Container maxWidth="sm" sx={{ textAlign: 'center', py: 10 }}>
                    <Typography variant="h3" gutterBottom>Tack!</Typography>
                    <Typography>
                        Vi har mottagit din felrapport{fel?.referens ? ` (${fel.referens})` : ''} och undersöker ärendet.
                    </Typography>
                    <Button variant="contained" sx={{ mt: 3 }} onClick={() => router.push(paths.dashboard.root)}>
                        Tillbaka till startsidan
                    </Button>
                </Container>
            </SimpleLayout>
        );
    }

    return (
        <SimpleLayout>
            <Container maxWidth="md" sx={{ py: 5 }}>
                <Typography variant="h3" gutterBottom>Rapportera ett problem</Typography>
                <Typography sx={{ mb: 4, color: 'text.secondary' }}>
                    Beskriv vad du gjorde när felet inträffade
                </Typography>

                {fel?.referens && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Referens: <strong>{fel.referens}</strong>
                    </Alert>
                )}

                {submitError && <Alert severity="error" sx={{ mb: 3 }}>{submitError}</Alert>}

                <Form methods={methods} onSubmit={handleSubmit(onSubmit)}>
                    <Box display="flex" flexDirection="column" gap={3}>
                        <RHFTextField
                            name="vadJagGjorde"
                            label="Vad försökte du göra?"
                            multiline
                            rows={2}
                            placeholder="Jag skulle ladda upp en leverantörsfaktura…"
                        />

                        <RHFTextField
                            name="vadHände"
                            label="Vad hände?"
                            multiline
                            rows={4}
                            placeholder="Sidan blev tom och det kom upp ett felmeddelande."
                        />

                        <RHFUpload
                            name="filer"
                            multiple
                            thumbnail
                            accept={{
                                'image/*': [],
                                'application/pdf': [],
                                'application/msword': [],
                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': []
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            loading={isSubmitting}
                        >
                            Skicka rapport
                        </Button>
                    </Box>
                </Form>
            </Container>
        </SimpleLayout>
    );
}
